// Feature: docs/features/coding-search-pre-rule-needs-review.md
// Feature: docs/features/icd10-coding-search.md
// Feature: docs/features/coding-search-needs-review.md
// Feature: docs/features/coding-search-patient-id.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  HStack,
  useToast,
  Spinner,
  Badge,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Link,
  Checkbox
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
const CodingSearchCpt = () => {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // Summary Data State
  const [summaryTimeframe, setSummaryTimeframe] = useState('200'); // '200' or '24h'
  const [summaryData, setSummaryData] = useState({
    payers: [],
    carriers: [],
    rules: [],
    cptModifiers: [],
    preCptModifiers: [],
    totalCases: 0
  });

  const [filters, setFilters] = useState(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return {
      pre_rule_code: '',
      pre_rule_modifier: '',
      post_rule_code: '',
      post_rule_modifier: '',
      rule_applied: '',
      payer_name: '',
      carrier_name: '',
      case_type: '',
      start_date: sevenDaysAgo.toISOString().split('T')[0],
      end_date: '',
      limit: 50,
      offset: 0,
      needs_review: false,
      pre_rule_needs_review: false,
      post_rule_needs_review: false
    };
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => {
      const newFilters = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'pre_rule_code' && !value) {
        newFilters.pre_rule_needs_review = false;
      }
      if (name === 'post_rule_code' && !value) {
        newFilters.post_rule_needs_review = false;
      }
      return newFilters;
    });
  };

  const handleSearch = async (overrideFilters = null) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      // If overrideFilters is an event object (from onClick), ignore it and use state filters
      const activeFilters = (overrideFilters && !overrideFilters.nativeEvent) ? overrideFilters : filters;
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await callApi(`/api/v1/coding-search?${params.toString()}`, accessToken);
      
      if (!response.ok) {
        throw new Error('Failed to fetch search results');
      }

      const data = await response.json();
      setResults(data.results || []);
      setTotalCount(data.total_count || 0);
      
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "Search Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSummaryData = async (timeframe = summaryTimeframe) => {
    setIsSummaryLoading(true);
    try {
      let url = `/api/v1/coding-search?limit=200`;
      
      if (timeframe === '24h') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const startDateStr = yesterday.toISOString().split('T')[0];
        url = `/api/v1/coding-search?limit=1000&start_date=${startDateStr}`;
      }

      const response = await callApi(url, accessToken);
      if (!response.ok) throw new Error('Failed to fetch summary data');
      
      const data = await response.json();
      const cases = data.results || [];
      
      // Aggregation Maps
      const payerCounts = {};
      const carrierCounts = {};
      const ruleCounts = {};
      const cptModCounts = {};
      const preCptModCounts = {};

      cases.forEach(c => {
        // Payers
        if (c.payer) {
          payerCounts[c.payer] = (payerCounts[c.payer] || 0) + 1;
        }
        
        // Carriers
        if (c.carrier) {
          carrierCounts[c.carrier] = (carrierCounts[c.carrier] || 0) + 1;
        }

        // Rules (Deduplicated per case)
        if (c.rules_applied && c.rules_applied.length > 0) {
          const uniqueRules = [...new Set(c.rules_applied.map(r => typeof r === 'string' ? r : r.type || JSON.stringify(r)))];
          uniqueRules.forEach(rule => {
            ruleCounts[rule] = (ruleCounts[rule] || 0) + 1;
          });
        }

        // Pre-Rules CPT + Modifiers
        if (c.pre_rules_codes && c.pre_rules_codes.length > 0) {
          c.pre_rules_codes.forEach(code => {
            let mods = '';
            if (Array.isArray(code.modifiers) && code.modifiers.length > 0) {
              mods = `-${code.modifiers.join('-')}`;
            } else if (typeof code.modifiers === 'string' && code.modifiers) {
              mods = `-${code.modifiers}`;
            } else if (typeof code.modifier === 'string' && code.modifier) {
              mods = `-${code.modifier}`;
            }
            const key = `${code.cpt_code}${mods}`;
            preCptModCounts[key] = (preCptModCounts[key] || 0) + 1;
          });
        }

        // Post-Rules CPT + Modifiers
        if (c.post_rules_codes && c.post_rules_codes.length > 0) {
          c.post_rules_codes.forEach(code => {
            let mods = '';
            if (Array.isArray(code.modifiers) && code.modifiers.length > 0) {
              mods = `-${code.modifiers.join('-')}`;
            } else if (typeof code.modifiers === 'string' && code.modifiers) {
              mods = `-${code.modifiers}`;
            } else if (typeof code.modifier === 'string' && code.modifier) {
              mods = `-${code.modifier}`;
            }
            const key = `${code.cpt_code}${mods}`;
            cptModCounts[key] = (cptModCounts[key] || 0) + 1;
          });
        }
      });

      // Convert to sorted arrays
      const sortObj = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));

      setSummaryData({
        payers: sortObj(payerCounts).slice(0, 15),
        carriers: sortObj(carrierCounts).slice(0, 15),
        rules: sortObj(ruleCounts).slice(0, 15),
        cptModifiers: sortObj(cptModCounts).slice(0, 15),
        preCptModifiers: sortObj(preCptModCounts).slice(0, 15),
        totalCases: cases.length
      });

    } catch (error) {
      console.error("Summary error:", error);
      toast({
        title: "Failed to load summary",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSummaryLoading(false);
    }
  };

  // Removed automatic loading of summary data
  // useEffect(() => {
  //   if (tabIndex === 1 && summaryData.totalCases === 0) {
  //     loadSummaryData();
  //   }
  // }, [tabIndex]);

  const handleTimeframeChange = (e) => {
    const newTimeframe = e.target.value;
    setSummaryTimeframe(newTimeframe);
    // Don't auto-load on timeframe change either, let the user click the button
  };

  const handleSummaryClick = (filterKey, value) => {
    // Parse CPT+Modifier if needed
    let newFilters = { ...filters, [filterKey]: value };
    
    if (filterKey === 'cpt_modifier') {
      const parts = value.split('-');
      newFilters = { 
        ...filters, 
        post_rule_code: parts[0], 
        post_rule_modifier: parts[1] || '' 
      };
    } else if (filterKey === 'pre_cpt_modifier') {
      const parts = value.split('-');
      newFilters = { 
        ...filters, 
        pre_rule_code: parts[0], 
        pre_rule_modifier: parts[1] || '' 
      };
    }

    setFilters(newFilters);
    setTabIndex(0); // Switch to Search tab
    handleSearch(newFilters); // Trigger search immediately
  };

  const handleClear = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    setFilters({
      pre_rule_code: '',
      pre_rule_modifier: '',
      post_rule_code: '',
      post_rule_modifier: '',
      rule_applied: '',
      payer_name: '',
      carrier_name: '',
      case_type: '',
      start_date: sevenDaysAgo.toISOString().split('T')[0],
      end_date: '',
      limit: 50,
      offset: 0,
      needs_review: false,
      pre_rule_needs_review: false,
      post_rule_needs_review: false
    });
    setResults([]);
    setTotalCount(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const isNeedsReview = (code) => {
    if (!code) return false;
    const type = (code.type || '').toLowerCase();
    const reason = (code.reason || '').toLowerCase();
    const justification = (code.justification || '').toLowerCase();
    
    return type.includes('preliminary') || 
           reason.includes('preliminary') || 
           reason.includes('for review') || 
           justification.includes('preliminary') || 
           justification.includes('for review');
  };

  const SummaryTable = ({ title, data, filterKey }) => (
    <Box bg="white" p={4} borderRadius="md" boxShadow="sm" borderWidth="1px">
      <Heading size="sm" mb={3} color="blue.700">{title}</Heading>
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>Name</Th>
            <Th isNumeric>Count</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((item, idx) => (
            <Tr 
              key={idx} 
              _hover={{ bg: "blue.50", cursor: "pointer" }}
              onClick={() => handleSummaryClick(filterKey, item.name)}
            >
              <Td fontSize="xs" maxW="200px" isTruncated title={item.name}>{item.name}</Td>
              <Td isNumeric fontWeight="bold">{item.count}</Td>
            </Tr>
          ))}
          {data.length === 0 && (
            <Tr><Td colSpan={2} textAlign="center" color="gray.500">No data</Td></Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue" variant="enclosed">
          <TabList>
            <Tab fontWeight="bold">Detailed Search</Tab>
            <Tab fontWeight="bold">Summary Dashboard</Tab>
          </TabList>

          <TabPanels>
            {/* SEARCH TAB */}
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {/* Filters Section */}
        <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px">
          <VStack spacing={4}>
            <HStack spacing={4} w="100%">
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Pre-Rule CPT</Text>
                <Input 
                  name="pre_rule_code" 
                  value={filters.pre_rule_code} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. 95870" 
                />
                <Checkbox 
                  mt={2}
                  size="sm"
                  name="pre_rule_needs_review" 
                  isChecked={filters.pre_rule_needs_review} 
                  onChange={handleFilterChange}
                  isDisabled={!filters.pre_rule_code}
                  colorScheme="orange"
                >
                  Needs Review Only
                </Checkbox>
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Post-Rule CPT</Text>
                <Input 
                  name="post_rule_code" 
                  value={filters.post_rule_code} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. 95870" 
                />
                <Checkbox 
                  mt={2}
                  size="sm"
                  name="post_rule_needs_review" 
                  isChecked={filters.post_rule_needs_review} 
                  onChange={handleFilterChange}
                  isDisabled={!filters.post_rule_code}
                  colorScheme="orange"
                >
                  Needs Review Only
                </Checkbox>
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Post-Rule Modifier</Text>
                <Input 
                  name="post_rule_modifier" 
                  value={filters.post_rule_modifier} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. XU" 
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Rule Applied</Text>
                <Input 
                  name="rule_applied" 
                  value={filters.rule_applied} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. remove_95941" 
                />
              </Box>
            </HStack>
            
            <HStack spacing={4} w="100%">
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Payer</Text>
                <Input 
                  name="payer_name" 
                  value={filters.payer_name} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. Medicare" 
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Carrier</Text>
                <Input 
                  name="carrier_name" 
                  value={filters.carrier_name} 
                  onChange={handleFilterChange} 
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. UHC" 
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Case Type</Text>
                <Select name="case_type" value={filters.case_type} onChange={handleFilterChange}>
                  <option value="">All Types</option>
                  <option value="IONM">IONM</option>
                  <option value="E/M">E/M</option>
                  <option value="Gastro">Gastro</option>
                  <option value="Clinical">Clinical</option>
                </Select>
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
                <Input 
                  type="date" 
                  name="start_date" 
                  value={filters.start_date} 
                  onChange={handleFilterChange} 
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
                <Input 
                  type="date" 
                  name="end_date" 
                  value={filters.end_date} 
                  onChange={handleFilterChange} 
                />
              </Box>
            </HStack>
            <HStack w="100%" justify="space-between" pt={2}>
              <Checkbox 
                name="needs_review" 
                isChecked={filters.needs_review} 
                onChange={handleFilterChange}
                colorScheme="blue"
              >
                <Text fontSize="sm" fontWeight="bold">Show Only Needs Review / Preliminary</Text>
              </Checkbox>
              <HStack>
                <Button colorScheme="blue" onClick={handleSearch} isLoading={isLoading} px={8}>
                  Search
                </Button>
                <Button variant="outline" onClick={handleClear} px={8}>
                  Clear
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {/* Results Section */}
        <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px" overflowX="auto">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md">Results</Heading>
            <Text fontSize="sm" color="gray.600">
              {totalCount > 0 ? `Showing ${results.length} of ${totalCount} cases` : 'No results'}
            </Text>
          </Flex>

          {isLoading ? (
            <Flex justify="center" p={10}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : results.length > 0 ? (
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Date</Th>
                  <Th>Case Details</Th>
                  <Th>Payer / Carrier</Th>
                  <Th>Pre-Rules Codes</Th>
                  <Th>Rules Applied</Th>
                  <Th>Post-Rules Codes</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {results.map((row, idx) => (
                  <Tr key={idx}>
                    <Td whiteSpace="nowrap">
                      {row.procedure_date ? new Date(row.procedure_date).toLocaleDateString() : 'N/A'}
                    </Td>
                    <Td>
                      <Text fontWeight="bold" fontSize="sm">{row.case_description || 'Unknown Case'}</Text>
                      <HStack mt={1}>
                        <Badge colorScheme="purple" fontSize="xs">{row.case_type || 'Unknown'}</Badge>
                        <Badge colorScheme={row.claim_type === 'pro' ? 'blue' : 'green'} fontSize="xs">
                          {row.claim_type?.toUpperCase() || 'STD'}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        ID:{' '}
                        <Link as={RouterLink} to={`/analyze-case-mn/${row.external_case_id}`} color="blue.500" target="_blank" rel="noopener noreferrer">
                          {row.external_case_id}
                        </Link>
                      </Text>
                      {row.external_patient_id && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Patient ID:{' '}
                          <Link as={RouterLink} to={`/ionmform?caseid=${row.external_case_id}`} color="blue.500" target="_blank" rel="noopener noreferrer">
                            {row.external_patient_id}
                          </Link>
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="bold">{row.payer || 'N/A'}</Text>
                      <Text fontSize="xs" color="gray.600">{row.carrier || 'N/A'}</Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={2}>
                        {row.pre_rules_codes && row.pre_rules_codes.map((code, cIdx) => {
                          const needsReview = isNeedsReview(code);
                          return (
                          <Box key={cIdx} p={2} bg={needsReview ? "orange.50" : "gray.50"} borderColor={needsReview ? "orange.200" : "inherit"} borderRadius="md" w="100%" borderWidth="1px">
                            <HStack mb={1}>
                              <Badge colorScheme={needsReview ? "orange" : "blue"} fontSize="sm">{code.cpt_code}</Badge>
                              {Array.isArray(code.modifiers) ? code.modifiers.map((mod, mIdx) => (
                                <Badge key={mIdx} colorScheme="orange" fontSize="xs">{mod}</Badge>
                              )) : (typeof code.modifiers === 'string' && code.modifiers ? (
                                <Badge colorScheme="orange" fontSize="xs">{code.modifiers}</Badge>
                              ) : (typeof code.modifier === 'string' && code.modifier ? (
                                <Badge colorScheme="orange" fontSize="xs">{code.modifier}</Badge>
                              ) : null))}
                              <Badge colorScheme="gray" fontSize="xs">Units: {code.units}</Badge>
                            </HStack>
                            {needsReview && (
                              <Text fontSize="xs" color="orange.800" mt={1} noOfLines={2} title={code.reason || code.justification}>
                                ⚠️ {code.reason || code.justification}
                              </Text>
                            )}
                          </Box>
                        )})}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {row.rules_applied && row.rules_applied.length > 0 ? (
                          [...new Set(row.rules_applied.map(rule => typeof rule === 'string' ? rule : rule.type || JSON.stringify(rule)))].map((ruleText, rIdx) => (
                            <Text key={rIdx} fontSize="xs" color="red.600" bg="red.50" p={1} borderRadius="sm" w="100%">
                              • {ruleText}
                            </Text>
                          ))
                        ) : (
                          <Text fontSize="xs" color="gray.400">No rules applied</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={2}>
                        {row.post_rules_codes && row.post_rules_codes.length > 0 ? (
                          row.post_rules_codes.map((code, cIdx) => {
                            const needsReview = isNeedsReview(code);
                            return (
                            <Box key={cIdx} p={2} bg={needsReview ? "orange.50" : "green.50"} borderRadius="md" w="100%" borderWidth="1px" borderColor={needsReview ? "orange.200" : "green.200"}>
                              <HStack mb={1}>
                                <Badge colorScheme={needsReview ? "orange" : "green"} fontSize="sm">{code.cpt_code}</Badge>
                                {Array.isArray(code.modifiers) ? code.modifiers.map((mod, mIdx) => (
                                  <Badge key={mIdx} colorScheme="orange" fontSize="xs">{mod}</Badge>
                                )) : (typeof code.modifiers === 'string' && code.modifiers ? (
                                  <Badge colorScheme="orange" fontSize="xs">{code.modifiers}</Badge>
                                ) : (typeof code.modifier === 'string' && code.modifier ? (
                                  <Badge colorScheme="orange" fontSize="xs">{code.modifier}</Badge>
                                ) : null))}
                                <Badge colorScheme="gray" fontSize="xs">Units: {code.units}</Badge>
                              </HStack>
                              {needsReview && (
                                <Text fontSize="xs" color="orange.800" mt={1} noOfLines={2} title={code.reason || code.justification}>
                                  ⚠️ {code.reason || code.justification}
                                </Text>
                              )}
                            </Box>
                          )})
                        ) : (
                          <Text fontSize="xs" color="gray.400">Same as Pre-Rules</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Button 
                        size="sm" 
                        colorScheme="blue" 
                        variant="outline"
                        onClick={() => navigate(`/case/${row.internal_case_id}`)}
                      >
                        View Case
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Flex justify="center" p={10}>
              <Text color="gray.500">Enter search criteria and click Search to view results.</Text>
            </Flex>
          )}
        </Box>
              </VStack>
            </TabPanel>

            {/* SUMMARY TAB */}
            <TabPanel px={0}>
              {isSummaryLoading ? (
                <Flex justify="center" p={10}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : (
                <VStack spacing={6} align="stretch">
                  <Box bg="white" p={5} borderRadius="md" boxShadow="sm" borderWidth="1px">
                    <Flex justify="space-between" align="center">
                      <StatGroup>
                        <Stat>
                          <StatLabel>Cases Analyzed</StatLabel>
                          <StatNumber>{summaryData.totalCases}</StatNumber>
                          <StatHelpText>Click any row below to view detailed cases</StatHelpText>
                        </Stat>
                      </StatGroup>
                      <HStack spacing={4} align="flex-end">
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" mb={1}>Timeframe</Text>
                          <Select size="sm" value={summaryTimeframe} onChange={handleTimeframeChange} w="200px">
                            <option value="200">Last 200 Cases</option>
                            <option value="24h">Last 24 Hours</option>
                          </Select>
                        </Box>
                        <Button 
                          colorScheme="blue" 
                          size="sm" 
                          onClick={() => loadSummaryData(summaryTimeframe)}
                          isLoading={isSummaryLoading}
                        >
                          Load Summary
                        </Button>
                      </HStack>
                    </Flex>
                  </Box>

                  {summaryData.totalCases > 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
                      <SummaryTable title="Top Payers" data={summaryData.payers} filterKey="payer_name" />
                      <SummaryTable title="Top Carriers" data={summaryData.carriers} filterKey="carrier_name" />
                      <SummaryTable title="Top Rules Applied" data={summaryData.rules} filterKey="rule_applied" />
                      <SummaryTable title="Top Initial CPTs" data={summaryData.preCptModifiers} filterKey="pre_cpt_modifier" />
                      <SummaryTable title="Top Final CPTs" data={summaryData.cptModifiers} filterKey="cpt_modifier" />
                    </SimpleGrid>
                  ) : (
                    <Flex justify="center" p={10}>
                      <Text color="gray.500">Click "Load Summary" to view the dashboard.</Text>
                    </Flex>
                  )}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default CodingSearchCpt;
